import { supabase } from './supabaseClient';
import { getProjectRelation, ProjectAccessRow, ProjectAccessUser } from './projectAccess';
import { isAdminRole, isSaveSettled } from './projectLockLogic';
import { rechtFehlerText } from './rechte/rechtFehler';
import type { RechteSicht } from './rechte/rechteService';

/**
 * Fremdzugriff mit Begruendung (SQL Nr. 128, docs/mitarbeiter-cockpit-plan.md).
 *
 * Wer ein Projekt oeffnet, fuer das er nicht zustaendig ist, sieht es zuerst
 * nur an. Fuer die erste Begehung oder Buchung braucht es eine kurze
 * Begruendung; sie gilt fuer dieses Projekt bis Mitternacht (Europe/Berlin —
 * das Ende rechnet der SERVER, nicht der Client). Eigentuemer und
 * Verantwortliche bekommt die RPC mitgeteilt; der Client benachrichtigt
 * niemanden selbst.
 *
 * Aufteilung wie bei projectLockLogic/projectLockService, nur in einer Datei:
 * oben die reinen Regeln (ohne Netz testbar, das Projekt hat kein jsdom),
 * unten die duenne Schicht ueber Tabelle und RPC.
 *
 * ─── Warum hier nichts einen Totalausfall ausloesen darf ───────────────────
 * Die Nummer 128 kann auf einer Umgebung fehlen (Testsystem hinterher, neue
 * Instanz). Dann gibt es weder Tabelle noch RPC. Das Nachschlagen liefert in
 * diesem Fall schlicht „keine Freigabe" — das Projekt bleibt Nur-Ansicht, wie
 * es fuer einen Fremden ohnehin richtig ist —, und erst das Freigeben meldet
 * ausdruecklich „SQL Nr. 128 fehlt". `ERWARTETE_MIGRATION` bleibt unberuehrt.
 *
 * ─── Mit dem Recht „Alle Projekte sehen" (Rollensystem, Nr. 154) ───────────
 * Wer das Recht laut Datenbank hat, begruendet nicht: beim Oeffnen holt der
 * Client die Freigabe still ueber `projekt_zugriff_per_recht` (fester Grund,
 * bis Mitternacht, ohne Mitteilung) und erneuert sie um Mitternacht ebenso.
 * Fehlt die RPC, bleibt es beim Begruendungsdialog — vor Nr. 154 also genau
 * wie heute. Die Rechte aus dem Rueckfall auf die Systemrolle zaehlen dafuer
 * NICHT: dort steht die Arbeitsvorbereitung mit „alle sehen", die Datenbank
 * raeumt ihr ohne Begruendung aber nichts ein.
 */

// ═══ Regeln ══════════════════════════════════════════════════════════════════

/** Mindestlaenge der Begruendung — dieselbe Wache steht in der RPC (22023). */
export const FOREIGN_REASON_MIN = 10;

/** Obergrenze, damit niemand versehentlich einen ganzen Bericht hineinkopiert. */
export const FOREIGN_REASON_MAX = 1000;

/** Eine Freigabezeile aus `public.project_foreign_access`. */
export interface ForeignAccessGrant {
    id: string;
    projectId: string;
    userId: string;
    /** Anzeigename zum Zeitpunkt der Freigabe; null = unbekannt. */
    userName: string | null;
    reason: string;
    companyId: string | null;
    /** ISO-8601. */
    createdAt: string;
    /** ISO-8601 — Mitternacht Europe/Berlin des Freigabetages, vom Server gesetzt. */
    validUntil: string;
    /**
     * Aus dem Recht „Alle Projekte sehen" (`projekt_zugriff_per_recht`) statt
     * aus einer Begruendung. Die RPC liefert nur das Ende — `id` ist dann leer.
     */
    viaRecht?: boolean;
}

/** Was die Regel vom Nutzer braucht: Kennung, E-Mail (Vertreter, Urlaub) und Rolle. */
export interface ForeignAccessUser extends ProjectAccessUser {
    role?: string | null;
}

/**
 * Ist das Projekt fuer diesen Nutzer fremd — braucht er zum Schreiben eine
 * Freigabe in `project_foreign_access` (begruendet oder ueber das Recht)?
 *
 * Nein, wenn …
 *   * kein Projekt oder kein Profil da ist. Ohne Profil ist die Rolle
 *     unbekannt, und „im Zweifel sperren" traefe genau die Admins, die
 *     serverseitig ohnehin ueberall schreiben duerfen. Entscheiden kann hier
 *     niemand; es bleibt beim bisherigen Weg, der Server hat das letzte Wort.
 *   * der Nutzer Admin oder Super-Admin ist (`is_admin()` in Journal-RLS und
 *     `acquire_project_lock`).
 *   * der Nutzer Freelancer ist. Der landet im Freelancer-Portal und oeffnet
 *     hier nie ein Projekt; die RPC wiese ihn mit 42501 ab. Ein Dialog, der
 *     nur scheitern kann, waere schlechter als keiner.
 *   * der Nutzer zustaendig ist — Eigentuemer, Koordinator, Vertreterliste,
 *     laufende Projektuebernahme oder Urlaubsvertretung im Zeitfenster.
 *     Dieselbe Quelle wie Projektliste und RLS (`getProjectRelation`).
 *
 * Wer ein fremdes Projekt in der Liste SIEHT (Recht „Alle Projekte sehen“,
 * im Rückfall die Arbeitsvorbereitung), darf es deshalb noch nicht
 * schreiben: der Server verlangt eine Freigabe in `project_foreign_access` —
 * mit dem Recht still über `projekt_zugriff_per_recht`, sonst begründet.
 *
 * @param date Stichtag fuer die Urlaubsvertretung (Tests geben ihn fest vor).
 */
export const isForeignProjectFor = (
    project: ProjectAccessRow | null | undefined,
    userProfile: ForeignAccessUser | null | undefined,
    date: Date = new Date(),
): boolean => {
    if (!project || !userProfile) return false;
    const role = userProfile.role ?? null;
    if (isAdminRole(role)) return false;
    if (role === 'freelancer') return false;
    return !getProjectRelation(project, userProfile, date).hasAccess;
};

/**
 * Braucht dieser Nutzer fuer dieses Projekt eine BEGRUENDUNG?
 *
 * Ja, wenn das Projekt fremd ist (`isForeignProjectFor`) — ausser er darf
 * ohne Begruendung oeffnen (`alleSehen`, siehe `darfOhneBegruendungOeffnen`).
 * Dann braucht er zwar weiter eine Freigabe, die holt aber
 * `grantForeignAccessByRight` still.
 *
 * @param alleSehen Recht „Alle Projekte sehen" laut Datenbank.
 */
export const needsForeignJustification = (
    project: ProjectAccessRow | null | undefined,
    userProfile: ForeignAccessUser | null | undefined,
    date: Date = new Date(),
    alleSehen = false,
): boolean => !alleSehen && isForeignProjectFor(project, userProfile, date);

/**
 * Darf ein fremdes Projekt ohne Begruendung geoeffnet werden?
 *
 * Nur mit dem Recht aus der DATENBANK (`quelle === 'db'`). Der Rueckfall auf
 * die Systemrolle gibt der Arbeitsvorbereitung zwar „alle sehen", die
 * Datenbank ohne Nr. 149/150 aber keine Freigabe ohne Begruendung — dort
 * bleibt es beim Dialog wie heute. Admins brauchen das nie (nie fremd).
 */
export const darfOhneBegruendungOeffnen = (
    rechte: Pick<RechteSicht, 'hat' | 'quelle'> | null | undefined,
): boolean => !!rechte && rechte.quelle === 'db' && rechte.hat('projekte.alle_sehen');

/** Die Begruendung, wie sie an den Server geht: ohne Rand-Leerraum. */
export const normalizeForeignReason = (text: string | null | undefined): string =>
    (text ?? '').trim();

/**
 * Laenge in ZEICHEN, nicht in UTF-16-Einheiten.
 *
 * `'🙂'.length` ist 2, Postgres zaehlt ein Zeichen. Mit `.length` bestuende
 * „🙂🙂🙂🙂🙂" die Pruefung hier und scheiterte dann am Server mit 22023 — die
 * Oberflaeche haette „gueltig" gezeigt und die Freigabe verweigert.
 */
export const foreignReasonLength = (text: string | null | undefined): number =>
    Array.from(normalizeForeignReason(text)).length;

/**
 * Deutscher Fehlertext — oder null, wenn die Begruendung taugt.
 *
 * Bewusst dieselbe Grenze wie die RPC: der Knopf darf erst freigeben, was der
 * Server auch annimmt.
 */
export const validateForeignReason = (text: string | null | undefined): string | null => {
    const len = foreignReasonLength(text);
    if (len === 0) return 'Bitte kurz begründen, warum Sie in diesem Projekt arbeiten.';
    if (len < FOREIGN_REASON_MIN) {
        return `Die Begründung braucht mindestens ${FOREIGN_REASON_MIN} Zeichen (noch ${FOREIGN_REASON_MIN - len}).`;
    }
    if (len > FOREIGN_REASON_MAX) {
        return `Die Begründung darf höchstens ${FOREIGN_REASON_MAX.toLocaleString('de-DE')} Zeichen lang sein.`;
    }
    return null;
};

/**
 * Gilt die Freigabe noch?
 *
 * Kaputter Zeitstempel = nein. Anders als bei der Projektsperre (fail-open)
 * ist hier „keine Freigabe" die harmlose Richtung: der Nutzer sieht das
 * Projekt weiter an und begruendet notfalls ein zweites Mal — die RPC gibt
 * dann die bestehende Zeile zurueck, ohne erneut zu benachrichtigen.
 */
export const isForeignGrantActive = (
    grant: Pick<ForeignAccessGrant, 'validUntil'> | null | undefined,
    nowMs: number,
): boolean => {
    if (!grant) return false;
    const until = Date.parse(grant.validUntil);
    return Number.isFinite(until) && until > nowMs;
};

/**
 * ═══ Ablauf der Freigabe im offenen Projekt ════════════════════════════════
 *
 * Die Freigabe endet um Mitternacht, das Projekt kann aber ueber Mitternacht
 * offen bleiben. Merkt der Client das nicht, arbeitet der Nutzer weiter im
 * Schreibmodus: die Sperre laesst ihn fail-open weiter (42501 gilt dort als
 * „keine Sperre bekannt"), und erst der Server weist jedes Speichern ab. Die
 * Aenderungen liegen dann in der Warteschlange fest, ohne dass jemand weiss,
 * warum. Deshalb faellt das Projekt beim Ablauf selbst in die Nur-Ansicht
 * zurueck und bietet die Begruendung neu an.
 */

/** Groesste Wartezeit, die `setTimeout` verkraftet (2^31 − 1 ms, gut 24 Tage). */
export const FOREIGN_EXPIRY_TIMER_MAX_MS = 2_147_483_647;

/**
 * Wie lange nach dem Ablauf auf einen gesicherten Speicherstand gewartet wird.
 *
 * Laeuft beim Ablauf gerade ein Speichern, soll es erst zu Ende kommen. Aber
 * nicht beliebig lange: nach Mitternacht nimmt der Server nichts mehr an, ein
 * 'error' oder 'queued' kaeme also nie mehr zur Ruhe. Verloren geht dabei
 * nichts — jede Eingabe steht schon beim Tippen in der Warteschlange
 * (App.handleUpdateReport) und geht nach der naechsten Begruendung hoch.
 */
export const FOREIGN_EXPIRY_SETTLE_GRACE_MS = 60_000;

/** Abstand der Nachschau, solange auf den Speicherstand gewartet wird. */
export const FOREIGN_EXPIRY_RECHECK_MS = 5_000;

/**
 * Restzeit der Freigabe in ms — 0, wenn sie abgelaufen ist; null ohne
 * Freigabe oder mit unlesbarem Ende (dann entscheidet allein der Server).
 */
export const msUntilGrantExpiry = (
    grant: Pick<ForeignAccessGrant, 'validUntil'> | null | undefined,
    nowMs: number,
): number | null => {
    if (!grant) return null;
    const until = Date.parse(grant.validUntil);
    if (!Number.isFinite(until)) return null;
    return Math.max(0, until - nowMs);
};

/** Was mit der Freigabe des offenen Projekts jetzt zu tun ist. */
export type ForeignGrantExpiry =
    /** nichts — keine Freigabe, Admin, oder das Projekt ist schon Nur-Ansicht */
    | { action: 'none' }
    /** noch gueltig bzw. Speichern laeuft noch: in `delayMs` erneut nachsehen */
    | { action: 'wait'; delayMs: number }
    /** abgelaufen: Sperre freigeben, Nur-Ansicht, Begruendung neu anbieten */
    | { action: 'expire' }
    /**
     * abgelaufen, aber das Recht „Alle Projekte sehen" ist da: still neu
     * holen (`grantForeignAccessByRight`); erst wenn das scheitert, 'expire'.
     */
    | { action: 'renew' };

export interface ForeignGrantExpiryInput {
    /** Die Freigabe, aus der der Schreibmodus des offenen Projekts stammt. */
    grant: Pick<ForeignAccessGrant, 'validUntil'> | null | undefined;
    nowMs: number;
    role: string | null | undefined;
    /** Schon Nur-Ansicht mit Begruendungsknopf? Dann gibt es nichts zu tun. */
    foreignPending: boolean;
    /** SaveStatus der Anwendung (siehe isSaveSettled in projectLockLogic). */
    saveStatus: string | null | undefined;
    settleGraceMs?: number;
    /**
     * Recht „Alle Projekte sehen" laut Datenbank (`darfOhneBegruendungOeffnen`).
     * Dann wird nicht zurueckgestuft, sondern erneuert.
     */
    alleSehen?: boolean;
}

/**
 * Die Entscheidung beim Ablauf — rein, damit sie ohne Zeitgeber pruefbar ist.
 *
 * Admins betrifft sie nie: sie schreiben serverseitig ohnehin ueberall und
 * bekommen gar keine Freigabe (isForeignProjectFor). Die Pruefung steht
 * trotzdem hier, damit ein Rollenwechsel mitten im Tag nichts falsch macht.
 *
 * Mit dem Recht „Alle Projekte sehen" wird sofort erneuert, ohne auf ein
 * laufendes Speichern zu warten: nach Mitternacht nimmt der Server ohne neue
 * Freigabe nichts mehr an, das Speichern kaeme also erst mit ihr zur Ruhe.
 */
export const decideForeignGrantExpiry = (input: ForeignGrantExpiryInput): ForeignGrantExpiry => {
    const { grant, nowMs, role, foreignPending, saveStatus } = input;
    const graceMs = input.settleGraceMs ?? FOREIGN_EXPIRY_SETTLE_GRACE_MS;
    if (isAdminRole(role) || foreignPending || !grant) return { action: 'none' };
    const until = Date.parse(grant.validUntil);
    if (!Number.isFinite(until)) return { action: 'none' };

    const left = until - nowMs;
    if (left > 0) return { action: 'wait', delayMs: Math.min(left, FOREIGN_EXPIRY_TIMER_MAX_MS) };
    if (input.alleSehen) return { action: 'renew' };

    // Abgelaufen. Ein laufendes Speichern darf noch zu Ende kommen — gedeckelt.
    const overdue = nowMs - until;
    if (!isSaveSettled(saveStatus) && overdue < graceMs) {
        return { action: 'wait', delayMs: Math.max(1, Math.min(FOREIGN_EXPIRY_RECHECK_MS, graceMs - overdue)) };
    }
    return { action: 'expire' };
};

/** snake_case aus der Datenbank nach camelCase; unbrauchbare Zeile = null. */
export const mapForeignGrantRow = (row: unknown): ForeignAccessGrant | null => {
    if (!row || typeof row !== 'object') return null;
    const r = row as Record<string, unknown>;
    const text = (v: unknown): string => (typeof v === 'string' ? v : '');
    const textOrNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
    const id = text(r.id);
    const projectId = text(r.project_id);
    const userId = text(r.user_id);
    if (!id || !projectId || !userId) return null;
    return {
        id,
        projectId,
        userId,
        userName: textOrNull(r.user_name),
        reason: text(r.reason),
        companyId: textOrNull(r.company_id),
        createdAt: text(r.created_at),
        validUntil: text(r.valid_until),
    };
};

/** Welche Art Fehler — die Oberflaeche entscheidet danach nichts, sie zeigt nur. */
export type ForeignAccessErrorKind =
    /** Tabelle oder RPC fehlen: SQL Nr. 128 ist auf dieser Umgebung nicht gefahren */
    | 'missing-migration'
    /** 42501 — keine Anmeldung oder Freelancer */
    | 'forbidden'
    /** 22023 — Begruendung zu kurz (auch die Vorpruefung im Client) */
    | 'reason'
    /** P0002 — Projekt existiert nicht (mehr) */
    | 'not-found'
    /** Netz weg, Server nicht erreichbar */
    | 'offline'
    | 'unknown';

/** Text fuer eine fehlende Nummer 128 — auch fuer die Admin-Liste. */
export const FOREIGN_SQL_FEHLT =
    'Fremdzugriff ist auf dieser Umgebung noch nicht eingerichtet (SQL Nr. 128 fehlt). ' +
    'Das Projekt bleibt in der Nur-Ansicht — bitte die Verwaltung informieren.';

export class ForeignAccessError extends Error {
    readonly kind: ForeignAccessErrorKind;
    readonly code: string | null;
    constructor(kind: ForeignAccessErrorKind, message: string, code: string | null = null) {
        super(message);
        this.name = 'ForeignAccessError';
        this.kind = kind;
        this.code = code;
    }
}

/** PostgREST/Postgres-Code eines Fehlers — oder ''. */
const errorCode = (error: unknown): string => {
    if (!error || typeof error !== 'object') return '';
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : '';
};

const errorText = (error: unknown): string => {
    if (typeof error === 'string') return error;
    if (!error || typeof error !== 'object') return '';
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
};

/**
 * Fehlt die Tabelle? 42P01 kommt von Postgres, PGRST205 von PostgREST (die
 * Tabelle steht nicht im Schema-Cache).
 */
export const isForeignAccessTableMissing = (error: unknown): boolean => {
    const code = errorCode(error);
    return code === '42P01' || code === 'PGRST205';
};

/**
 * Fehlt die RPC? PGRST202 von PostgREST (Funktion unbekannt), 42883 von
 * Postgres (Funktion mit dieser Signatur existiert nicht).
 */
export const isForeignAccessRpcMissing = (error: unknown): boolean => {
    const code = errorCode(error);
    return code === 'PGRST202' || code === '42883';
};

/** fetch scheitert je nach Browser mit anderem Wortlaut — alle meinen „kein Netz". */
const OFFLINE_MUSTER = /failed to fetch|networkerror|load failed|fetch failed|network request failed/i;

/**
 * Serverfehler in Art und deutschen Text uebersetzen. Rein, damit die
 * Zuordnung ohne Netz pruefbar ist.
 */
export const classifyForeignAccessError = (
    error: unknown,
): { kind: ForeignAccessErrorKind; message: string } => {
    if (error instanceof ForeignAccessError) return { kind: error.kind, message: error.message };

    const code = errorCode(error);
    if (isForeignAccessRpcMissing(error) || isForeignAccessTableMissing(error)) {
        return { kind: 'missing-migration', message: FOREIGN_SQL_FEHLT };
    }
    if (code === '42501') {
        return {
            kind: 'forbidden',
            message: 'Keine Berechtigung für einen Fremdzugriff. Bitte neu anmelden oder die Verwaltung fragen.',
        };
    }
    if (code === '22023') {
        return {
            kind: 'reason',
            message: `Die Begründung ist zu kurz — mindestens ${FOREIGN_REASON_MIN} Zeichen.`,
        };
    }
    if (code === 'P0002') {
        return {
            kind: 'not-found',
            message: 'Das Projekt wurde nicht gefunden. Möglicherweise wurde es inzwischen gelöscht.',
        };
    }

    const text = errorText(error);
    if (OFFLINE_MUSTER.test(text)) {
        return {
            kind: 'offline',
            message: 'Keine Verbindung zum Server. Bitte die Verbindung prüfen und erneut versuchen.',
        };
    }
    return {
        kind: 'unknown',
        message: text
            ? `Der Fremdzugriff konnte nicht freigegeben werden: ${text}`
            : 'Der Fremdzugriff konnte nicht freigegeben werden. Bitte erneut versuchen.',
    };
};

const toForeignAccessError = (error: unknown): ForeignAccessError => {
    if (error instanceof ForeignAccessError) return error;
    const { kind, message } = classifyForeignAccessError(error);
    return new ForeignAccessError(kind, message, errorCode(error) || null);
};

// ─── Freigabe ueber das Recht „Alle Projekte sehen" (Nr. 154) ──────────────

/** Der feste Grund, den `projekt_zugriff_per_recht` in die Zeile schreibt. */
export const FOREIGN_GRUND_RECHT = 'Recht „Alle Projekte sehen“ (ohne Begründung)';

/** Ergebnis der stillen Freigabe beim Oeffnen bzw. um Mitternacht. */
export type ForeignByRightResult =
    /** Freigabe steht — das Projekt geht beschreibbar auf. */
    | { outcome: 'granted'; grant: ForeignAccessGrant }
    /**
     * Kein Urteil des Servers, mit dem sich arbeiten liesse: die RPC fehlt
     * (Nr. 154 nicht gefahren) oder ihr Ende liegt nach der Uhr des Clients
     * schon hinter uns. Dann gilt der Weg von heute — Nur-Ansicht, Begruendung.
     */
    | { outcome: 'fallback' }
    /** Der Server hat abgelehnt oder ist nicht erreichbar — Projekt NICHT oeffnen. */
    | { outcome: 'error'; kind: ForeignAccessErrorKind; message: string };

/**
 * Antwort von `projekt_zugriff_per_recht` deuten. Rein, damit jede Lage ohne
 * Netz pruefbar ist.
 *
 * `returns timestamptz` kommt als Zeichenkette; eine Liste mit einem Wert
 * (aeltere PostgREST-Fassungen) gilt genauso.
 */
export const interpretForeignByRightResponse = (
    projectId: string,
    userId: string,
    response: { data: unknown; error: unknown },
    nowMs: number,
): ForeignByRightResult => {
    const { data, error } = response;
    if (error) {
        if (isForeignAccessRpcMissing(error)) return { outcome: 'fallback' };
        const { kind, message } = classifyForeignAccessError(error);
        if (kind === 'missing-migration') return { outcome: 'fallback' };
        if (kind === 'forbidden') {
            // „fehlendes Recht projekte.alle_sehen" (Recht inzwischen entzogen)
            // oder fremde Firma — nur das erste ist ein Fall fuer den Admin.
            const rechtText = typeof error === 'object'
                ? rechtFehlerText(error as { code?: string; message?: string })
                : null;
            return {
                outcome: 'error',
                kind,
                message: rechtText
                    ? `${rechtText} Ein Admin kann es unter Verwaltung → Benutzer & Rechte → Rollen & Rechte vergeben.`
                    : 'Dieses Projekt dürfen Sie nicht öffnen (keine Berechtigung).',
            };
        }
        if (kind === 'unknown') {
            const text = errorText(error);
            return {
                outcome: 'error',
                kind,
                message: text
                    ? `Das Projekt konnte nicht geöffnet werden: ${text}`
                    : 'Das Projekt konnte nicht geöffnet werden. Bitte erneut versuchen.',
            };
        }
        return { outcome: 'error', kind, message };
    }
    const wert = Array.isArray(data) ? data[0] : data;
    const until = typeof wert === 'string' ? Date.parse(wert) : NaN;
    if (!Number.isFinite(until)) {
        return {
            outcome: 'error',
            kind: 'unknown',
            message: 'Der Server hat keine gültige Freigabe zurückgegeben. Bitte erneut versuchen.',
        };
    }
    if (until <= nowMs) return { outcome: 'fallback' };
    return {
        outcome: 'granted',
        grant: {
            id: '',
            projectId,
            userId,
            userName: null,
            reason: FOREIGN_GRUND_RECHT,
            companyId: null,
            createdAt: new Date(nowMs).toISOString(),
            validUntil: new Date(until).toISOString(),
            viaRecht: true,
        },
    };
};

// ═══ Datenbank ═══════════════════════════════════════════════════════════════

const GRANT_COLUMNS = 'id, project_id, user_id, user_name, reason, company_id, created_at, valid_until';

/**
 * Zeitlimit fuer das Nachschlagen beim Oeffnen.
 *
 * Dieselbe Groesse wie `LOCK_ACQUIRE_TIMEOUT_MS` und aus demselben Grund: das
 * Nachschlagen liegt VOR dem Laden des Projekts und verzoegert jeden Klick.
 * Laeuft es ab, gilt „keine Freigabe" — das Projekt geht zum Ansehen auf, und
 * eine erneute Begruendung kostet keine zweite Mitteilung.
 */
export const FOREIGN_GRANT_LOOKUP_TIMEOUT_MS = 4_000;

/** Einmal warnen, dann Ruhe — das Nachschlagen laeuft bei jedem Oeffnen. */
let missingTableWarned = false;

const warnMissingTableOnce = (error: unknown) => {
    if (missingTableWarned) return;
    missingTableWarned = true;
    console.warn('[foreignAccess] project_foreign_access fehlt (SQL Nr. 128 nicht gefahren):', error);
};

/**
 * Die heute noch gueltige Freigabe des Nutzers fuer dieses Projekt — oder null.
 *
 * Wirft NIE. Fehlende Tabelle, Netzfehler und Zeitlimit liefern alle null:
 * fuer einen Fremden ist die Nur-Ansicht der richtige Ausfall, und genau
 * dorthin fuehrt „keine Freigabe".
 *
 * `valid_until > jetzt` prueft der Server gegen die Uhr des Clients; der
 * Eintrag selbst traegt die vom Server gerechnete Mitternacht.
 */
export const fetchActiveForeignGrant = async (
    projectId: string,
    userId: string,
    timeoutMs: number = FOREIGN_GRANT_LOOKUP_TIMEOUT_MS,
): Promise<ForeignAccessGrant | null> => {
    if (!projectId || !userId) return null;

    const lookup = async (): Promise<ForeignAccessGrant | null> => {
        try {
            const { data, error } = await supabase
                .from('project_foreign_access')
                .select(GRANT_COLUMNS)
                .eq('project_id', projectId)
                .eq('user_id', userId)
                .gt('valid_until', new Date().toISOString())
                .order('valid_until', { ascending: false })
                .limit(1);
            if (error) {
                if (isForeignAccessTableMissing(error)) warnMissingTableOnce(error);
                else console.warn('[foreignAccess] Freigabe konnte nicht gelesen werden:', error);
                return null;
            }
            const grant = mapForeignGrantRow(Array.isArray(data) ? data[0] : data);
            return isForeignGrantActive(grant, Date.now()) ? grant : null;
        } catch (e) {
            console.warn('[foreignAccess] Freigabe konnte nicht gelesen werden:', e);
            return null;
        }
    };

    // Kein Promise.race mit reject: der Verlierer bliebe als unbehandelte
    // Ablehnung liegen. Es gewinnt schlicht, wer zuerst resolve ruft.
    return new Promise<ForeignAccessGrant | null>((resolve) => {
        let done = false;
        const finish = (value: ForeignAccessGrant | null) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve(value);
        };
        const timer = setTimeout(() => {
            console.warn('[foreignAccess] Freigabe nicht rechtzeitig gelesen — Projekt geht zum Ansehen auf');
            finish(null);
        }, timeoutMs);
        lookup().then(finish, () => finish(null));
    });
};

/**
 * Freigabe fuer heute anlegen — oder die bestehende zurueckbekommen (die RPC
 * ist fuer denselben Tag idempotent und benachrichtigt nur beim ersten Mal).
 *
 * Wirft `ForeignAccessError` mit deutschem Text; die Art steht in `kind`.
 * Die Begruendung wird VORHER geprueft, damit ein leerer Klick gar nicht erst
 * als 22023 vom Server zurueckkommt.
 */
export const grantForeignAccess = async (
    projectId: string,
    reason: string,
): Promise<ForeignAccessGrant> => {
    const problem = validateForeignReason(reason);
    if (problem) throw new ForeignAccessError('reason', problem);
    if (!projectId) throw new ForeignAccessError('not-found', 'Kein Projekt geöffnet.');

    let response: { data: unknown; error: unknown };
    try {
        response = await supabase.rpc('grant_foreign_project_access', {
            p_project_id: projectId,
            p_reason: normalizeForeignReason(reason),
        });
    } catch (e) {
        throw toForeignAccessError(e);
    }
    if (response.error) {
        console.warn('[foreignAccess] Freigabe abgelehnt:', response.error);
        throw toForeignAccessError(response.error);
    }

    // `returns project_foreign_access` kommt als Objekt, `returns setof …`
    // als Liste mit einer Zeile — beides ist dieselbe Antwort.
    const data = response.data;
    const grant = mapForeignGrantRow(Array.isArray(data) ? data[0] : data);
    if (!grant) {
        throw new ForeignAccessError(
            'unknown',
            'Der Server hat keine Freigabe zurückgegeben. Bitte erneut versuchen.',
        );
    }
    return grant;
};

/**
 * Zeitlimit fuer die stille Freigabe. Laenger als das Nachschlagen: hier
 * haengt das Oeffnen selbst daran, und ein Abbruch heisst „Projekt geht nicht
 * auf". Ein zweiter Versuch ist harmlos — die RPC ist je Tag idempotent.
 */
export const FOREIGN_BY_RIGHT_TIMEOUT_MS = 8_000;

/**
 * Freigabe ueber das Recht „Alle Projekte sehen" holen — ohne Begruendung,
 * ohne Mitteilung, gueltig bis Mitternacht (Nr. 154).
 *
 * Wirft NIE; die Lage steht im Ergebnis (`interpretForeignByRightResponse`).
 * Zeitlimit und Netzfehler sind 'error' (Klartext, Projekt bleibt zu).
 */
export const grantForeignAccessByRight = async (
    projectId: string,
    userId: string,
    timeoutMs: number = FOREIGN_BY_RIGHT_TIMEOUT_MS,
): Promise<ForeignByRightResult> => {
    if (!projectId) {
        return { outcome: 'error', kind: 'not-found', message: 'Kein Projekt geöffnet.' };
    }
    const anfrage = async (): Promise<ForeignByRightResult> => {
        try {
            const response = await supabase.rpc('projekt_zugriff_per_recht', { p_project_id: projectId });
            if (response.error) console.warn('[foreignAccess] Freigabe ueber das Recht abgelehnt:', response.error);
            return interpretForeignByRightResponse(projectId, userId, response, Date.now());
        } catch (e) {
            return interpretForeignByRightResponse(projectId, userId, { data: null, error: e }, Date.now());
        }
    };
    // Wie beim Nachschlagen: wer zuerst resolve ruft, gewinnt — ohne
    // unbehandelte Ablehnung des Verlierers.
    return new Promise<ForeignByRightResult>((resolve) => {
        let done = false;
        const finish = (value: ForeignByRightResult) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve(value);
        };
        const timer = setTimeout(() => {
            console.warn('[foreignAccess] Freigabe ueber das Recht nicht rechtzeitig beantwortet');
            finish({
                outcome: 'error',
                kind: 'offline',
                message: 'Der Server antwortet gerade nicht. Bitte die Verbindung prüfen und erneut versuchen.',
            });
        }, timeoutMs);
        anfrage().then(finish, (e) => finish(interpretForeignByRightResponse(projectId, userId, { data: null, error: e }, Date.now())));
    });
};

/** Ergebnis fuer die Admin-Liste. */
export interface ForeignAccessLog {
    rows: ForeignAccessGrant[];
    /** Tabelle fehlt — SQL Nr. 128 nicht gefahren. */
    missing: boolean;
    /** Sonstiger Lesefehler als deutscher Text; null = alles gut. */
    error: string | null;
}

/** Wie weit die Admin-Liste zurueckschaut. */
export const FOREIGN_LOG_DAYS = 30;

/**
 * Die Fremdzugriffe der letzten Tage, neueste zuerst.
 *
 * Wer was sieht, entscheidet die RLS (eigene Zeilen, Admin der Firma,
 * Super-Admin alles) — hier wird nichts gefiltert. Wirft nie.
 */
export const fetchForeignAccessLog = async (
    days: number = FOREIGN_LOG_DAYS,
    limit = 500,
): Promise<ForeignAccessLog> => {
    const since = new Date(Date.now() - Math.max(1, days) * 24 * 60 * 60 * 1000).toISOString();
    try {
        const { data, error } = await supabase
            .from('project_foreign_access')
            .select(GRANT_COLUMNS)
            .gte('created_at', since)
            .order('created_at', { ascending: false })
            .limit(limit);
        if (error) {
            if (isForeignAccessTableMissing(error)) return { rows: [], missing: true, error: null };
            console.warn('[foreignAccess] Liste konnte nicht gelesen werden:', error);
            return { rows: [], missing: false, error: 'Die Fremdzugriffe konnten nicht geladen werden.' };
        }
        const rows = (Array.isArray(data) ? data : [])
            .map(mapForeignGrantRow)
            .filter((g): g is ForeignAccessGrant => g !== null);
        return { rows, missing: false, error: null };
    } catch (e) {
        console.warn('[foreignAccess] Liste konnte nicht gelesen werden:', e);
        return { rows: [], missing: false, error: 'Die Fremdzugriffe konnten nicht geladen werden.' };
    }
};
